import { useState, useEffect, useRef } from 'react'
import { Printer, Search, BarChart3, Loader2 } from 'lucide-react'
import clientesService from '../../services/clientesService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try {
    const [y, m, d] = s.split('-')
    return `${d}/${m}/${y}`
  } catch { return s }
}

// Períodos predefinidos
function getPeriodos() {
  const hoje = new Date()
  const ano = hoje.getFullYear()
  const mes = hoje.getMonth()

  const fmt = d => d.toISOString().split('T')[0]
  const inicio = (y, m) => fmt(new Date(y, m, 1))
  const fim = (y, m) => fmt(new Date(y, m + 1, 0))

  return [
    { label: 'Último mês', de: inicio(ano, mes - 1), ate: fim(ano, mes - 1) },
    { label: 'Últimos 3 meses', de: inicio(ano, mes - 3), ate: fmt(hoje) },
    { label: 'Últimos 6 meses', de: inicio(ano, mes - 6), ate: fmt(hoje) },
    { label: 'Último ano', de: inicio(ano - 1, mes), ate: fmt(hoje) },
    { label: 'Ano atual', de: inicio(ano, 0), ate: fmt(hoje) },
    { label: 'Ano anterior', de: inicio(ano - 1, 0), ate: fim(ano - 1, 11) },
    { label: 'Personalizado', de: '', ate: '' },
  ]
}

const SITUACOES = [
  { value: 'todos', label: 'Todos' },
  { value: 'venda', label: 'Venda' },
  { value: 'compra', label: 'Compra' },
  { value: 'defesa', label: 'Defesa' },
]

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { borderBottom: '2px solid #1f2937', paddingBottom: 12, marginBottom: 18 },
  hdrT1: { fontSize: 18, fontWeight: 700 },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 10 },
  th: { borderBottom: '2px solid #d1d5db', padding: '6px 6px', textAlign: 'left', fontWeight: 700, fontSize: 10 },
  thC: { borderBottom: '2px solid #d1d5db', padding: '6px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '6px 6px', textAlign: 'right', fontWeight: 700, fontSize: 10 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '5px 6px', verticalAlign: 'middle', fontSize: 11 },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '5px 6px', textAlign: 'center', verticalAlign: 'middle', fontSize: 11 },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '5px 6px', textAlign: 'right', verticalAlign: 'middle', fontSize: 11 },
  leilaoRow: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  leilaoTd: { padding: '6px 6px', fontSize: 10, color: '#374151', fontWeight: 600 },
  totRow: { fontWeight: 700, borderTop: '2px solid #1f2937', background: '#f3f4f6' },
  subTotRow: { fontWeight: 600, background: '#f9fafb', borderTop: '1px solid #d1d5db' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

export default function BalancoTab({ cliente, organizadorId }) {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorId || organizadorAtivo?.id

  const periodos = getPeriodos()
  const [periodoIdx, setPeriodoIdx] = useState(4) // Ano atual
  const [dataInicio, setDataInicio] = useState(periodos[4].de)
  const [dataFim, setDataFim] = useState(periodos[4].ate)
  const [situacao, setSituacao] = useState('todos')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  const handlePeriodoChange = (idx) => {
    setPeriodoIdx(idx)
    if (idx < periodos.length - 1) {
      setDataInicio(periodos[idx].de)
      setDataFim(periodos[idx].ate)
    }
  }

  const fetchDados = async () => {
    if (!orgId || !cliente?.id || !dataInicio || !dataFim) return
    setLoading(true)
    try {
      const data = await clientesService.getBalanco(orgId, cliente.id, {
        data_inicio: dataInicio,
        data_fim: dataFim,
        situacao
      })
      setDados(data)
    } catch (err) {
      console.error('Erro ao buscar balanço:', err)
      setDados(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (orgId && cliente?.id && dataInicio && dataFim && periodoIdx < periodos.length - 1) {
      fetchDados()
    }
  }, [orgId, cliente?.id, situacao]) // eslint-disable-line

  const handleImprimir = async () => {
    if (!orgId || !cliente?.id || !dataInicio || !dataFim) return
    setLoadingPdf(true)
    try {
      const blob = await clientesService.getBalancoPdf(orgId, cliente.id, {
        data_inicio: dataInicio,
        data_fim: dataFim,
        situacao
      })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      console.error('Erro ao gerar PDF:', err)
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdf = () => {
    setShowPdf(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  // Não exibe se cliente não foi salvo
  if (!cliente) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm">Salve o cliente primeiro para visualizar o balanço.</p>
      </div>
    )
  }

  const situacaoLabel = SITUACOES.find(s => s.value === situacao)?.label || situacao

  // Totais gerais
  const totalLotes = dados?.leiloes?.reduce((s, l) => s + l.lotes.length, 0) || 0
  const totalCabecas = dados?.leiloes?.reduce((s, l) => s + l.lotes.reduce((sl, lt) => sl + (lt.qtd || 0), 0), 0) || 0
  const totalValor = dados?.leiloes?.reduce((s, l) => s + l.lotes.reduce((sl, lt) => sl + (lt.valor_total || 0), 0), 0) || 0

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Situação */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Situação</label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {SITUACOES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSituacao(s.value)}
                  className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                    situacao === s.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Período */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Período</label>
            <select
              value={periodoIdx}
              onChange={(e) => handlePeriodoChange(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {periodos.map((p, i) => (
                <option key={i} value={i}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Datas personalizadas */}
          {periodoIdx === periodos.length - 1 && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">De</label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={e => setDataInicio(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">Até</label>
                <input
                  type="date"
                  value={dataFim}
                  onChange={e => setDataFim(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <button
            onClick={fetchDados}
            disabled={loading || !dataInicio || !dataFim}
            className="flex items-center gap-2 px-4 py-2 h-9 bg-gray-800 hover:bg-gray-900 disabled:bg-gray-200 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Search size={15} /> Pesquisar
          </button>

          <div className="ml-auto">
            <button
              onClick={handleImprimir}
              disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione os filtros e clique em Pesquisar
        </div>
      ) : dados.leiloes?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhum registro encontrado para o período e situação selecionados
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-auto">
          <div style={S.page}>
            {/* Header */}
            <div style={{ ...S.hdr, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ minWidth: 140 }}>
                {organizadorAtivo?.logo_url && (
                  <img
                    src={organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`}
                    alt="logo"
                    style={{ maxHeight: 70, maxWidth: 160, objectFit: 'contain' }}
                  />
                )}
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 16px' }}>
                <div style={S.hdrT1}>BALANÇO DO CLIENTE - {situacaoLabel.toUpperCase()}S</div>
                <div style={S.hdrT2}>{cliente.codigo ? `${cliente.codigo} - ` : ''}{cliente.nome}</div>
                <div style={S.hdrT3}>Período: {fmtDate(dataInicio)} a {fmtDate(dataFim)}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={{ maxHeight: 70, maxWidth: 160, objectFit: 'contain' }} />
              </div>
            </div>

            {/* Tabela por leilão */}
            {dados.leiloes.map((leilao, idx) => (
              <div key={idx} style={{ marginBottom: 16 }}>
                {/* Cabeçalho do leilão */}
                <div style={{ background: '#f3f4f6', padding: '6px 8px', borderRadius: 4, marginBottom: 4, fontSize: 11, fontWeight: 600, color: '#1f2937' }}>
                  LEILÃO: {leilao.codigo} {leilao.nome} &nbsp;&nbsp; LOCAL: {leilao.local || '—'} &nbsp;&nbsp; DATA: {fmtDate(leilao.data)}
                </div>

                <table style={S.tbl}>
                  <thead>
                    <tr>
                      <th style={{ ...S.thC, width: '6%' }}>Lote</th>
                      <th style={{ ...S.th, width: '22%' }}>{situacao === 'venda' ? 'Comprador' : situacao === 'compra' ? 'Vendedor' : 'Contraparte'}</th>
                      <th style={{ ...S.thC, width: '6%' }}>Qtd</th>
                      <th style={{ ...S.thC, width: '6%' }}>Sexo</th>
                      <th style={{ ...S.thC, width: '10%' }}>Idade</th>
                      <th style={{ ...S.th, width: '14%' }}>Raça</th>
                      <th style={{ ...S.thR, width: '12%' }}>Unitário</th>
                      <th style={{ ...S.thR, width: '14%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leilao.lotes.map((lote, li) => (
                      <tr key={li}>
                        <td style={S.tdC}>{String(lote.numero).padStart(4, '0')}</td>
                        <td style={S.td}>{lote.contraparte || '—'}</td>
                        <td style={S.tdC}>{lote.qtd}</td>
                        <td style={S.tdC}>{lote.sexo || '—'}</td>
                        <td style={S.tdC}>{lote.idade || '—'}</td>
                        <td style={S.td}>{lote.raca || '—'}</td>
                        <td style={S.tdR}>{brl(lote.valor_unitario)}</td>
                        <td style={S.tdR}>{brl(lote.valor_total)}</td>
                      </tr>
                    ))}
                    {/* Subtotal do leilão */}
                    <tr style={S.subTotRow}>
                      <td style={{ ...S.td, borderTop: '1px solid #d1d5db' }} colSpan={2}>
                        <strong>TOTAL DE LOTES: {leilao.lotes.length}</strong>
                      </td>
                      <td style={{ ...S.tdC, borderTop: '1px solid #d1d5db' }}>
                        <strong>{leilao.lotes.reduce((s, l) => s + (l.qtd || 0), 0)}</strong>
                      </td>
                      <td style={{ ...S.td, borderTop: '1px solid #d1d5db' }} colSpan={3}>
                        <strong>
                          MACHOS: {leilao.lotes.reduce((s, l) => s + (l.sexo === 'M' ? (l.qtd || 0) : 0), 0)}
                          &nbsp;&nbsp; FÊMEAS: {leilao.lotes.reduce((s, l) => s + (l.sexo === 'F' ? (l.qtd || 0) : 0), 0)}
                        </strong>
                      </td>
                      <td style={{ ...S.tdR, borderTop: '1px solid #d1d5db' }}>
                        <strong>MÉDIA: {brl(leilao.lotes.reduce((s, l) => s + (l.valor_unitario || 0), 0) / (leilao.lotes.length || 1))}</strong>
                      </td>
                      <td style={{ ...S.tdR, borderTop: '1px solid #d1d5db' }}>
                        <strong>{brl(leilao.lotes.reduce((s, l) => s + (l.valor_total || 0), 0))}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}

            {/* Total Geral */}
            <div style={{ borderTop: '2px solid #1f2937', paddingTop: 10, marginTop: 10 }}>
              <table style={{ ...S.tbl, marginBottom: 0 }}>
                <tbody>
                  <tr style={S.totRow}>
                    <td style={{ ...S.td, width: '40%' }}>
                      <strong>TOTAL GERAL: {totalLotes} lotes</strong>
                    </td>
                    <td style={{ ...S.tdC, width: '15%' }}>
                      <strong>{totalCabecas} cabeças</strong>
                    </td>
                    <td style={{ ...S.tdR, width: '20%' }}>
                      <strong>Média: {brl(totalValor / (totalCabecas || 1))}</strong>
                    </td>
                    <td style={{ ...S.tdR, width: '25%' }}>
                      <strong>Valor Total: {brl(totalValor)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Balanço do Cliente - {situacaoLabel}s</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Printer size={15} /> Imprimir
                </button>
                <button
                  onClick={closePdf}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Balanço do Cliente" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
